import { useState, useCallback } from "react";
import { GenericFilterBody } from "./generic-filter-body";
import { Button } from "../button/button";
import { DropdownMenu } from "../dropdown-menu/dropdown-menu";
import { FilterButton } from "../filter-button/filter-button";
import type { SelectOption, SelectOptionValue } from "../inputs/select/select";
import type {
  DataTableFilters,
  DataTableSetColumnFilter,
  FilterData,
} from "../table/data-table.types";

export type FilterConfig<TData, TFilterKey extends keyof TData = keyof TData> = {
  key: TFilterKey;
  title: string;
  options: SelectOption<string | number>[];
  columnKey?: string;
  className?: string;
};

export type GenericFilterValues<TData, TFilterKey extends keyof TData = keyof TData> = Record<
  TFilterKey,
  SelectOptionValue[]
>;

type Props<TData, TFilterKey extends keyof TData = keyof TData> = {
  setColumnFilter: DataTableSetColumnFilter<TData>;
  filters: DataTableFilters<TData>;
  filterConfigs: FilterConfig<TData, TFilterKey>[];
  title?: string;
  minWidth?: string;
  gridCols?: string;
};

export const GenericFilter = <TData, TFilterKey extends keyof TData = keyof TData>({
  setColumnFilter,
  filters,
  filterConfigs,
  title = "Filter",
  minWidth = "min-w-[600px]",
  gridCols = "grid-cols-3",
}: Props<TData, TFilterKey>) => {
  const buildFilters = useCallback(
    (valueMapper: (config: FilterConfig<TData, TFilterKey>) => SelectOptionValue[]) =>
      filterConfigs.reduce(
        (acc, config) => {
          acc[config.key] = valueMapper(config);
          return acc;
        },
        {} as GenericFilterValues<TData, TFilterKey>
      ),
    [filterConfigs]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<GenericFilterValues<TData, TFilterKey>>(() =>
    buildFilters(() => [])
  );
  const [localFilters, setLocalFilters] = useState<GenericFilterValues<TData, TFilterKey>>(() =>
    buildFilters(() => [])
  );
  const syncFiltersFromProps = () => {
    const currentFilters = buildFilters(
      (config) => (filters[config.key] as FilterData)?.values || []
    );
    setAppliedFilters(currentFilters);
    setLocalFilters(currentFilters);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      syncFiltersFromProps();
    } else {
      setLocalFilters(appliedFilters);
    }
  };

  const handleCheckboxChange = (
    category: TFilterKey,
    value: SelectOptionValue,
    checked: boolean
  ) => {
    setLocalFilters((prev) => {
      const currentValues = prev[category] || [];

      const config = filterConfigs.find((c) => c.key === category);
      const isNumeric = config?.options.some((opt) => typeof opt.value === "number");

      const normalizedValue = isNumeric ? Number(value) : value;

      const newValues = checked
        ? [...currentValues, normalizedValue]
        : currentValues.filter((v: SelectOptionValue) => {
            return isNumeric ? v !== Number(value) : v !== value;
          });

      return {
        ...prev,
        [category]: newValues,
      } as GenericFilterValues<TData, TFilterKey>;
    });
  };

  const handleApplyFilters = (filtersData?: GenericFilterValues<TData, TFilterKey>) => {
    const localFiltersData = filtersData || localFilters;

    filterConfigs.forEach((config) => {
      const filterValues = localFiltersData[config.key];
      setColumnFilter(config.key, filterValues, config.columnKey);
    });

    setAppliedFilters(localFiltersData);
    setLocalFilters(localFiltersData);
    setIsOpen(false);
  };

  const handleClear = (apply = false) => {
    const clearedFilters = buildFilters(() => []);
    setLocalFilters(clearedFilters);

    if (apply) {
      handleApplyFilters(clearedFilters);
    }
  };

  const handleCancel = () => {
    setLocalFilters(appliedFilters);
    setIsOpen(false);
  };

  const currentFilterCount = Object.values(localFilters).reduce<number>(
    (sum, values) => sum + (values as SelectOptionValue[]).length,
    0
  );

  const appliedFilterCount = Object.values(appliedFilters).reduce<number>(
    (sum, values) => sum + (values as SelectOptionValue[]).length,
    0
  );

  const hasChanges = JSON.stringify(localFilters) !== JSON.stringify(appliedFilters);

  return (
    <DropdownMenu
      trigger={
        <FilterButton
          filterCount={appliedFilterCount}
          showClear={appliedFilterCount > 0}
          onClearFilters={() => handleClear(true)}
          onOpenFilters={() => {}}
        />
      }
      align="right"
      placement="bottom"
      className="max-w-2xl [&_button]:hover:bg-transparent [&_button]:hover:text-inherit"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className={`${minWidth} p-6 max-md:min-w-[400px] max-sm:min-w-[350px]`}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>

        <GenericFilterBody
          localFilters={localFilters}
          filterConfigs={filterConfigs}
          onCheckboxChange={handleCheckboxChange}
          gridCols={gridCols}
        />

        <div className="mt-6 flex flex-row items-end justify-between border-t border-gray-100 pt-4">
          <div className="w-full">
            <Button
              onClick={() => handleClear(false)}
              size={"sm"}
              variant="ghost"
              className="float-left"
              disabled={currentFilterCount === 0}
            >
              Reset Filters
            </Button>
          </div>
          <div className="flex w-full justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={() => handleApplyFilters()} disabled={!hasChanges}>
              {hasChanges ? "Apply" : "Applied"} Filters{" "}
              {currentFilterCount > 0 && `(${currentFilterCount})`}
            </Button>
          </div>
        </div>
      </div>
    </DropdownMenu>
  );
};
